import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, runBulk } from "@/components/common/BulkActionBar";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { AccountTagsManager } from "@/components/admin/AccountTagsManager";
import { PostTypesManager } from "@/components/admin/PostTypesManager";

const SPECIAL_ACCOUNT_TAGS = "__account_tags__";
const SPECIAL_POST_TYPES = "__post_types__";

const pretty = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const SORTS = [
  { value: "label_asc", label: "Label A–Z" },
  { value: "label_desc", label: "Label Z–A" },
  { value: "active", label: "Active first" },
];

export default function AdminReference() {
  const [collections, setCollections] = useState([]);
  const [coll, setColl] = useState("");
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState(null); // {id, label}
  const [del, setDel] = useState(null); // item
  const [sort, setSort] = useState("label_asc");
  const [bulkDel, setBulkDel] = useState(false);

  useEffect(() => {
    api.get("/admin/reference-collections").then((r) => {
      setCollections(r.data || []);
      if (r.data?.length) setColl(r.data[0].name);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!coll || coll === SPECIAL_ACCOUNT_TAGS || coll === SPECIAL_POST_TYPES) return;
    setItems(null);
    try { const { data } = await api.get(`/admin/reference/${coll}`); setItems(data); }
    catch { setItems([]); }
  }, [coll]);
  // Fetch on collection change via the IIFE+active pattern (no synchronous setState in effect).
  useEffect(() => {
    if (!coll || coll === SPECIAL_ACCOUNT_TAGS || coll === SPECIAL_POST_TYPES) return;
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/admin/reference/${coll}`); if (active) setItems(data); }
      catch { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [coll]);

  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    const l = (x) => (x.label || "").toLowerCase();
    if (sort === "label_desc") arr.sort((a, b) => l(b).localeCompare(l(a)));
    else if (sort === "active") arr.sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0) || l(a).localeCompare(l(b)));
    else arr.sort((a, b) => l(a).localeCompare(l(b)));
    return arr;
  }, [items, sort]);

  const ids = useMemo(() => sorted.map((i) => i.id), [sorted]);
  const sel = useRowSelection(ids);
  useEffect(() => { sel.clear(); /* eslint-disable-next-line */ }, [coll]);

  const add = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try { await api.post(`/admin/reference/${coll}`, { label: newLabel.trim() }); setNewLabel(""); toast.success("Added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setAdding(false); }
  };

  const toggleActive = async (it) => {
    try { await api.patch(`/admin/reference/${coll}/${it.id}`, { active: !it.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setColor = async (it, color) => {
    try { await api.patch(`/admin/reference/${coll}/${it.id}`, { color }); toast.success("Color updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveRename = async () => {
    try { await api.patch(`/admin/reference/${coll}/${edit.id}`, { label: edit.label }); setEdit(null); toast.success("Renamed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/reference/${coll}/${del.id}?hard=${del.system ? "false" : "true"}`); setDel(null); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkRemove = async () => {
    const selItems = sorted.filter((it) => sel.isSelected(it.id));
    await runBulk(selItems, (it) => api.delete(`/admin/reference/${coll}/${it.id}?hard=${it.system ? "false" : "true"}`), "Removed");
    sel.clear(); setBulkDel(false); load();
  };

  return (
    <div data-testid="admin-reference" className="space-y-4">
      <div className="flex items-center justify-end">
        <AdminRefreshButton testId="admin-refresh-reference" onRefresh={load} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Card className="border-0 p-5">
          <label className="text-sm font-[560] text-foreground">Reference collection</label>
          <Select value={coll} onValueChange={setColl}>
            <SelectTrigger className="mt-2" data-testid="admin-reference-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={SPECIAL_ACCOUNT_TAGS} data-testid="admin-reference-option-account-tags">★ Account Tags</SelectItem>
              <SelectItem value={SPECIAL_POST_TYPES} data-testid="admin-reference-option-post-types">★ Post Types</SelectItem>
              {collections.map((c) => <SelectItem key={c.name} value={c.name}>{pretty(c.name)} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-3 text-xs text-muted-foreground">These values power the dropdowns and multi-selects across profiles, events and resources.</p>
        </Card>
      </div>

      <div className="lg:col-span-8">
        {coll === SPECIAL_ACCOUNT_TAGS ? <AccountTagsManager /> : coll === SPECIAL_POST_TYPES ? <PostTypesManager /> : (
        <Card className="border-0 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-[640] text-foreground">{coll ? pretty(coll) : "Values"}</h2>
            <div className="flex items-center gap-3">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9 w-40" data-testid="admin-reference-sort"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{items ? `${items.length} values` : ""}</span>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a new value…" data-testid="admin-reference-new-input" />
            <Button onClick={add} disabled={adding || !newLabel.trim()} data-testid="admin-reference-add">{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Add</span></Button>
          </div>

          {!items ? <div className="mt-6"><Loading /></div> : (
            <>
              {items.length > 0 && (
                <label className="mt-4 flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox checked={sel.allSelected} onCheckedChange={sel.toggleAll} data-testid="admin-reference-select-all" /> Select all
                </label>
              )}
              <div className="mt-2 divide-y divide-border/70">
                {sorted.map((it) => (
                  <div key={it.id} className={`flex items-center gap-3 py-2.5 ${it.active ? "" : "opacity-50"}`} data-testid={`admin-ref-item-${it.value}`}>
                    <Checkbox checked={sel.isSelected(it.id)} onCheckedChange={() => sel.toggle(it.id)} data-testid={`admin-ref-select-${it.value}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {coll === "profile_categories" && it.color && <span className="inline-block h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: it.color }} />}
                        <div className="truncate text-sm font-[540] text-foreground">{it.label}</div>
                      </div>
                      <div className="truncate font-mono text-[11px] text-muted-foreground">{it.value}{it.system ? " · system" : ""}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {coll === "profile_categories" && (
                        <input
                          type="color"
                          key={it.color || "none"}
                          defaultValue={it.color || "#64748b"}
                          onBlur={(e) => { if (e.target.value !== (it.color || "#64748b")) setColor(it, e.target.value); }}
                          title="Badge color"
                          className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
                          data-testid={`admin-ref-color-${it.value}`}
                        />
                      )}
                      <Switch checked={!!it.active} onCheckedChange={() => toggleActive(it)} data-testid={`admin-ref-active-${it.value}`} />
                      <Button size="sm" variant="ghost" onClick={() => setEdit({ id: it.id, label: it.label })} data-testid={`admin-ref-edit-${it.value}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDel(it)} data-testid={`admin-ref-delete-${it.value}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No values yet.</p>}
              </div>
            </>
          )}
        </Card>
        )}
      </div>

      <BulkActionBar count={sel.count} onClear={sel.clear}>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setBulkDel(true)} data-testid="bulk-delete"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
      </BulkActionBar>

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {sel.count} value{sel.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>System values are deactivated (hidden from forms but kept for existing records); custom values are permanently removed. Existing records that reference them won&apos;t be changed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="bulk-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rename value</DialogTitle></DialogHeader>
          <Input value={edit?.label || ""} onChange={(e) => setEdit((s) => ({ ...s, label: e.target.value }))} data-testid="admin-ref-rename-input" />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={saveRename} data-testid="admin-ref-rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &ldquo;{del?.label}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {del?.system
                ? "This is a system value — it will be deactivated (hidden from forms) but kept for existing records."
                : "This custom value will be permanently removed. Existing records that reference it won't be changed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="admin-ref-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
